import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { activitiesAPI, closeCircleAPI, calendarAPI, preferencesAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/calendar.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CELL_HEIGHT = 56;

function HourRangePanel({ current, onSave, onClose }) {
  const [start, setStart] = useState(current.start);
  const [end, setEnd]     = useState(current.end);
  const hourLabel = h => h === 24 ? 'Midnight' : h === 12 ? '12 PM' : h > 12 ? `${h - 12} PM` : `${h} AM`;

  useEffect(() => {
    const handler = (e) => { if (!e.target.closest('.cal-hour-panel')) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div className="cal-hour-panel">
      <div className="cal-hour-panel-title">View hours</div>
      <div className="cal-hour-row">
        <label>From</label>
        <select value={start} onChange={e => setStart(Number(e.target.value))}>
          {Array.from({ length: 13 }, (_, i) => i).map(h => (
            <option key={h} value={h}>{hourLabel(h)}</option>
          ))}
        </select>
      </div>
      <div className="cal-hour-row">
        <label>To</label>
        <select value={end} onChange={e => setEnd(Number(e.target.value))}>
          {Array.from({ length: 13 }, (_, i) => i + 12).map(h => (
            <option key={h} value={h}>{hourLabel(h)}</option>
          ))}
        </select>
      </div>
      <div className="cal-hour-panel-actions">
        <button className="cal-hour-save" onClick={() => onSave(start, end)}>Apply</button>
        <button className="cal-hour-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function loadHourRange() {
  return {
    start: parseInt(localStorage.getItem('cal_hour_start') || '6', 10),
    end:   parseInt(localStorage.getItem('cal_hour_end')   || '24', 10),
  };
}

const EXTERNAL_COLOR = { bg: '#f3f0ff', text: '#5b21b6', border: '#c4b5fd' };

const FRIEND_COLORS = [
  { bg: '#e8f5e9', text: '#52AAD8', border: '#a5d6a7' },
  { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  { bg: '#fce4ec', text: '#880e4f', border: '#f48fb1' },
  { bg: '#e8eaf6', text: '#283593', border: '#9fa8da' },
  { bg: '#e0f7fa', text: '#006064', border: '#80deea' },
  { bg: '#f3e5f5', text: '#4a148c', border: '#ce93d8' },
];

function hashColor(userId) {
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return FRIEND_COLORS[h % FRIEND_COLORS.length];
}

function getWeekBounds(anchor) {
  const d = new Date(anchor);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - ((day + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { monday, sunday };
}

function getMonthBounds(anchor) {
  const y = anchor.getFullYear(), m = anchor.getMonth();
  const start = new Date(y, m, 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(y, m + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function getMonthGridDays(anchor) {
  const { start, end } = getMonthBounds(anchor);
  const gridStart = new Date(start);
  const firstDow = (start.getDay() + 6) % 7;
  gridStart.setDate(start.getDate() - firstDow);
  const gridEnd = new Date(end);
  const lastDow = (end.getDay() + 6) % 7;
  if (lastDow < 6) gridEnd.setDate(end.getDate() + (6 - lastDow));
  const days = [];
  const cur = new Date(gridStart);
  while (cur <= gridEnd) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
  return days;
}

function formatMonthLabel(anchor) {
  return anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function toISO(date) {
  return date.toISOString().split('T')[0];
}

function formatWeekLabel(monday, sunday) {
  const opts = { month: 'short', day: 'numeric' };
  const m = monday.toLocaleDateString('en-US', opts);
  const s = sunday.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  return `${m} – ${s}`;
}

export default function Calendar() {
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');
  const [anchor, setAnchor] = useState(() => new Date());
  const [viewMode, setViewMode] = useState('week');
  const [activities, setActivities] = useState([]);
  const [externalEvents, setExternalEvents] = useState([]);
  const [connections, setConnections] = useState([]);
  const [ccBusy, setCcBusy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [showHourSettings, setShowHourSettings] = useState(false);
  const [hourRange, setHourRange] = useState(loadHourRange);

  const HOUR_START = hourRange.start;
  const HOUR_END   = hourRange.end;
  const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

  const saveHourRange = (start, end) => {
    const clamped = { start: Math.max(0, Math.min(start, 22)), end: Math.min(24, Math.max(end, start + 1)) };
    localStorage.setItem('cal_hour_start', clamped.start);
    localStorage.setItem('cal_hour_end', clamped.end);
    setHourRange(clamped);
    setShowHourSettings(false);
    preferencesAPI.update({ cal_hour_start: clamped.start, cal_hour_end: clamped.end }).catch(() => {});
  };

  const { monday, sunday } = getWeekBounds(anchor);

  const load = useCallback(async () => {
    setLoading(true);
    const fromDate = viewMode === 'month' ? toISO(getMonthBounds(anchor).start) : toISO(monday);
    const toDate   = viewMode === 'month' ? toISO(getMonthBounds(anchor).end)   : toISO(sunday);
    try {
      const [feedRes, ccRes, extRes, connRes] = await Promise.all([
        activitiesAPI.getFeed(500, 0, fromDate, toDate),
        closeCircleAPI.getAll(),
        calendarAPI.getEvents(fromDate, toDate),
        calendarAPI.getConnections(),
      ]);
      setActivities(feedRes.data || []);
      setExternalEvents(extRes.data || []);
      setConnections(connRes.data || []);

      const accepted = (ccRes.data || []).filter((m) => m.status === 'accepted');
      const busyResults = await Promise.all(
        accepted.map(async (m) => {
          const memberId = m.user_id === currentUserId ? m.trusted_user_id : m.user_id;
          try {
            const r = await closeCircleAPI.getAvailability(memberId);
            return { userId: memberId, displayName: m.display_name, blocks: r.data || [] };
          } catch {
            return { userId: memberId, displayName: m.display_name, blocks: [] };
          }
        })
      );
      setCcBusy(busyResults);

      const staleConns = (connRes.data || []).filter(c => {
        if (c.provider === 'ics_file') return false;
        if (!c.last_synced_at) return true;
        return (Date.now() - new Date(c.last_synced_at)) > 30 * 60 * 1000;
      });
      if (staleConns.length > 0) {
        await Promise.allSettled(staleConns.map(c => calendarAPI.syncConnection(c.id)));
        const reloaded = await calendarAPI.getEvents(fromDate, toDate);
        setExternalEvents(reloaded.data || []);
      }
    } catch (err) {
      console.error('Failed to load calendar:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toISO(anchor), viewMode]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    preferencesAPI.get().then(r => {
      const s = r.data?.cal_hour_start;
      const e = r.data?.cal_hour_end;
      if (s != null && e != null) {
        setHourRange({ start: s, end: e });
        localStorage.setItem('cal_hour_start', s);
        localStorage.setItem('cal_hour_end', e);
      }
    }).catch(() => {});
  }, []);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    const syncable = connections.filter(c => c.provider !== 'ics_file');
    await Promise.allSettled(syncable.map(c => calendarAPI.syncConnection(c.id)));
    const fromDate = viewMode === 'month' ? toISO(getMonthBounds(anchor).start) : toISO(monday);
    const toDate   = viewMode === 'month' ? toISO(getMonthBounds(anchor).end)   : toISO(sunday);
    const reloaded = await calendarAPI.getEvents(fromDate, toDate);
    setExternalEvents(reloaded.data || []);
    setSyncingAll(false);
  };

  const prevWeek  = () => { const d = new Date(anchor); d.setDate(d.getDate() - 7); setAnchor(d); };
  const nextWeek  = () => { const d = new Date(anchor); d.setDate(d.getDate() + 7); setAnchor(d); };
  const prevMonth = () => { const d = new Date(anchor); d.setMonth(d.getMonth() - 1); setAnchor(d); };
  const nextMonth = () => { const d = new Date(anchor); d.setMonth(d.getMonth() + 1); setAnchor(d); };
  const goToday   = () => setAnchor(new Date());

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });

  const todayStr = toISO(new Date());

  // ── Week view data ──
  const eventsByDayHour = {};
  activities.forEach((a) => {
    if (!a.start_time) return;
    const start = new Date(a.start_time);
    const dayIdx = (start.getDay() + 6) % 7;
    const hour = start.getHours();
    const minutes = start.getMinutes();
    const duration = a.duration_minutes || 60;
    if (hour < HOUR_START || hour >= HOUR_END) return;
    const key = `${dayIdx}-${hour}`;
    if (!eventsByDayHour[key]) eventsByDayHour[key] = [];
    eventsByDayHour[key].push({ ...a, _dayIdx: dayIdx, _hour: hour, _minutes: minutes, _duration: duration, _isExternal: false });
  });

  externalEvents.filter(e => e.is_all_day === 0).forEach((e) => {
    if (!e.start_time) return;
    const start = new Date(e.start_time);
    const dayIdx = (start.getDay() + 6) % 7;
    const hour = start.getHours();
    const minutes = start.getMinutes();
    const duration = e.duration_minutes || 60;
    if (hour < HOUR_START || hour >= HOUR_END) return;
    const key = `${dayIdx}-${hour}`;
    if (!eventsByDayHour[key]) eventsByDayHour[key] = [];
    eventsByDayHour[key].push({ ...e, _dayIdx: dayIdx, _hour: hour, _minutes: minutes, _duration: duration, _isExternal: true });
  });

  const isOwn = (a) => a.creator_id === currentUserId;

  const ccBusyCells = new Set();
  ccBusy.forEach(({ blocks }) => {
    blocks.forEach((b) => {
      if (!b.start_time) return;
      const start = new Date(b.start_time);
      const dayIdx = (start.getDay() + 6) % 7;
      const startHour = start.getHours();
      const endHour = startHour + Math.ceil((b.duration_minutes || 60) / 60);
      for (let h = startHour; h < Math.min(endHour, HOUR_END); h++) {
        ccBusyCells.add(`${dayIdx}-${h}`);
      }
    });
  });

  // ── Month view data ──
  const eventsByDay = {};
  activities.forEach(ev => {
    if (!ev.start_time) return;
    const ds = toISO(new Date(ev.start_time));
    if (!eventsByDay[ds]) eventsByDay[ds] = [];
    eventsByDay[ds].push({ ...ev, _isExternal: false });
  });
  externalEvents.forEach(ev => {
    if (!ev.start_time) return;
    const ds = toISO(new Date(ev.start_time));
    if (!eventsByDay[ds]) eventsByDay[ds] = [];
    eventsByDay[ds].push({ ...ev, _isExternal: true });
  });

  const monthGridDays = getMonthGridDays(anchor);
  const currentMonth = anchor.getMonth();
  const hasSyncableConns = connections.some(c => c.provider !== 'ics_file');

  return (
    <Layout title="Calendar">
    <div className="calendar-container">
      <header className="calendar-header">
        <div className="cal-nav">
          <button className="btn-nav" onClick={viewMode === 'month' ? prevMonth : prevWeek}>‹</button>
          <span className="cal-week-label">
            {viewMode === 'month' ? formatMonthLabel(anchor) : formatWeekLabel(monday, sunday)}
          </span>
          <button className="btn-nav" onClick={viewMode === 'month' ? nextMonth : nextWeek}>›</button>
          <button className="btn-nav" onClick={goToday}>Today</button>
          <button
            className="btn-nav btn-nav-active"
            onClick={() => setViewMode(v => v === 'week' ? 'month' : 'week')}
          >
            {viewMode === 'week' ? 'Month' : 'Week'}
          </button>
        </div>

        <div className="cal-header-right">
          {hasSyncableConns && (
            <button className="btn-nav" onClick={handleSyncAll} disabled={syncingAll}>
              {syncingAll ? '…' : '↻'}
            </button>
          )}
          <button className="btn-nav" onClick={() => navigate('/calendar-settings')}>
            Calendars
          </button>
          <div style={{ position: 'relative' }}>
            <button className="btn-nav" onClick={() => setShowHourSettings(v => !v)} title="View hours">
              {HOUR_START}:00 – {HOUR_END === 24 ? 'midnight' : `${HOUR_END}:00`}
            </button>
            {showHourSettings && (
              <HourRangePanel current={hourRange} onSave={saveHourRange} onClose={() => setShowHourSettings(false)} />
            )}
          </div>
          <div className="cal-legend">
            <span><span className="legend-dot" style={{ background: '#52AAD8' }} />You</span>
            <span><span className="legend-dot" style={{ background: '#52AAD8' }} />Friends</span>
            {externalEvents.length > 0 && (
              <span><span className="legend-dot" style={{ background: '#5b21b6' }} />Imported</span>
            )}
          </div>
        </div>
      </header>

      <div className="calendar-body">
        {loading ? (
          <p className="cal-empty">Loading…</p>
        ) : viewMode === 'month' ? (
          /* ── MONTH VIEW ── */
          <div className="cal-month-wrap">
            <div className="cal-month-day-names">
              {DAYS.map(d => <div key={d} className="cal-month-day-name">{d}</div>)}
            </div>
            <div className="cal-month-grid">
              {monthGridDays.map(day => {
                const ds = toISO(day);
                const inMonth = day.getMonth() === currentMonth;
                const isToday = ds === todayStr;
                const dayEvs = (eventsByDay[ds] || []).sort((a, b) => {
                  if (!a._isExternal && b._isExternal) return -1;
                  if (a._isExternal && !b._isExternal) return 1;
                  return (a.start_time || '').localeCompare(b.start_time || '');
                });
                return (
                  <div
                    key={ds}
                    className={`cal-month-cell${inMonth ? '' : ' out-month'}${isToday ? ' month-today' : ''}`}
                    onClick={() => { setAnchor(day); setViewMode('week'); }}
                  >
                    <div className={`cal-month-date-num${isToday ? ' is-today' : ''}`}>
                      {day.getDate()}
                    </div>
                    <div className="cal-month-events">
                      {dayEvs.slice(0, 3).map((ev, i) => {
                        const color = ev._isExternal
                          ? '#5b21b6'
                          : isOwn(ev) ? '#52AAD8' : '#52AAD8';
                        return (
                          <div
                            key={i}
                            className="cal-month-event-chip"
                            style={{ background: color }}
                            onClick={e => {
                              e.stopPropagation();
                              if (!ev._isExternal) navigate(`/activity/${ev.id}`);
                            }}
                          >
                            {ev.title || ev.activity_type || '(event)'}
                          </div>
                        );
                      })}
                      {dayEvs.length > 3 && (
                        <div className="cal-month-more">+{dayEvs.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ── WEEK VIEW ── */
          <div className="calendar-grid-wrap">
            <div className="cal-day-headers">
              <div className="cal-corner" />
              {weekDays.map((d, i) => {
                const ds = toISO(d);
                return (
                  <div key={i} className="cal-day-header">
                    <div className="day-name">{DAYS[i]}</div>
                    <div className={`day-num${ds === todayStr ? ' today' : ''}`}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="cal-time-grid">
              {HOURS.map((hour) => (
                <React.Fragment key={hour}>
                  <div className="cal-time-label">
                    {hour === 12 ? '12pm' : hour > 12 ? `${hour - 12}pm` : `${hour}am`}
                  </div>
                  {weekDays.map((d, dayIdx) => {
                    const key = `${dayIdx}-${hour}`;
                    const events = eventsByDayHour[key] || [];
                    const ds = toISO(d);
                    return (
                      <div
                        key={dayIdx}
                        className={`cal-cell${ds === todayStr ? ' today-col' : ''}${ccBusyCells.has(`${dayIdx}-${hour}`) ? ' cc-busy' : ''}`}
                      >
                        {events.map((ev, ei) => {
                          const topPct = (ev._minutes / 60) * CELL_HEIGHT;
                          const heightPx = Math.max(20, (ev._duration / 60) * CELL_HEIGHT - 2);
                          const color = ev._isExternal
                            ? EXTERNAL_COLOR
                            : isOwn(ev)
                              ? { bg: '#52AAD8', text: '#fff', border: '#1558d6' }
                              : hashColor(ev.creator_id);
                          return (
                            <div
                              key={ev._isExternal ? `ext-${ev.id}-${ei}` : ev.id}
                              className="cal-event"
                              style={{
                                top: topPct,
                                height: heightPx,
                                background: color.bg,
                                color: color.text,
                                borderLeft: `3px solid ${color.border || color.bg}`,
                                cursor: ev._isExternal ? 'default' : 'pointer',
                              }}
                              onClick={ev._isExternal ? undefined : () => navigate(`/activity/${ev.id}`)}
                              title={ev._isExternal
                                ? `${ev.title}${ev.calendar_label ? ` · ${ev.calendar_label}` : ''}`
                                : `${ev.title || ev.activity_type}${isOwn(ev) ? '' : ` · ${ev.creator_display_name}`}`
                              }
                            >
                              <div className="cal-event-title">{ev.title || ev.activity_type}</div>
                              {ev._isExternal && ev.calendar_label && (
                                <div className="cal-event-sub">{ev.calendar_label}</div>
                              )}
                              {!ev._isExternal && !isOwn(ev) && (
                                <div className="cal-event-sub">{ev.creator_display_name}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
